import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * SystemBlockTile — visual card for a SYSTEM block in the pipeline canvas.
 *
 * Two modes from the Phase 2 mockup (.sys-chip):
 *   - Normal:      slate accent (accent-system colour), "SYSTEM" tag
 *   - Polymorphic: teal accent (accent-method colour), "Poly" tag via PolymorphicBadge
 *
 * Slot (`children`) accepts InlineEditField rows so SystemBlockTile composes
 * naturally with the issue_session block's TTL fields.
 *
 * All colour values are DS tokens; no raw hex.
 *
 * Composes: PolymorphicBadge (when polymorphic=true).
 */

export interface SystemBlockTileProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Block identifier name (e.g. "issue_session", "verify_credential"). */
  name: string
  /** Sub-line below the title (e.g. "mints tokens · emits auth.login_succeeded"). */
  subtitle: string
  /**
   * When true, renders the teal polymorphic variant with the "Poly" tag and
   * an optional methods sub-strip.
   */
  polymorphic?: boolean
  /**
   * When `polymorphic=true`, the list of method names this block handles.
   * Rendered as a dashed-top strip: "handles password · email_otp · …"
   */
  methods?: string[]
  /**
   * Optional icon slot — typically a Lucide icon component instance.
   * Rendered in the 26×26 icon square on the left of the title row.
   */
  icon?: React.ReactNode
  /**
   * Optional children slot — renders below the subtitle.
   * Use this for InlineEditField rows (e.g. issue_session TTL fields).
   */
  children?: React.ReactNode
}

export const SystemBlockTile = React.forwardRef<HTMLDivElement, SystemBlockTileProps>(
  ({ name, subtitle, polymorphic = false, methods, icon, children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        // Base: dark card surface with gradient header, matches mockup bg-sys
        'relative w-full rounded-[10px]',
        'bg-gradient-to-b from-ia-surface-canvas/55 to-ia-elevated',
        'border border-ia-border-subtle',
        // 2px accent top stripe (::before pattern)
        'before:absolute before:inset-x-0 before:top-0 before:h-[2px]',
        'before:rounded-t-[10px]',
        polymorphic
          ? 'before:bg-[#2dd4bf] before:opacity-70'
          : 'before:bg-ia-text-tertiary before:opacity-50',
        'p-[14px]',
        className,
      )}
      {...props}
    >
      {/* Head row: icon + title + tag */}
      <div className="flex items-center gap-2.5 mb-1.5">
        {/* Icon square */}
        <div
          aria-hidden="true"
          className={cn(
            'flex h-[26px] w-[26px] items-center justify-center rounded-[5px] shrink-0',
            polymorphic
              ? 'bg-[rgba(45,212,191,0.06)] text-[#2dd4bf]'
              : 'bg-ia-text-tertiary/5 text-ia-text-tertiary',
          )}
        >
          {icon && <span className="[&_svg]:h-[13px] [&_svg]:w-[13px]">{icon}</span>}
        </div>

        {/* Title */}
        <span className="flex-1 truncate font-mono text-[12px] text-ia-text-secondary">{name}</span>

        {/* Tag — "Poly" (teal) or "System" (slate) */}
        {polymorphic ? (
          <span
            className={cn(
              'shrink-0 font-mono text-[8.5px] uppercase tracking-widest',
              'px-[6px] py-[2px] rounded-sm',
              'text-[#2dd4bf] bg-[rgba(45,212,191,0.06)] border border-[rgba(45,212,191,0.22)]',
            )}
          >
            Poly
          </span>
        ) : (
          <span
            className={cn(
              'shrink-0 font-mono text-[8.5px] uppercase tracking-widest',
              'px-[6px] py-[2px] rounded-sm',
              'text-ia-text-tertiary bg-ia-text-tertiary/6 border border-ia-text-tertiary/20',
            )}
          >
            System
          </span>
        )}
      </div>

      {/* Sub-line */}
      <p className="pl-[36px] font-mono text-[10px] text-ia-text-muted leading-[1.45]">
        {subtitle}
      </p>

      {/* Methods strip (polymorphic only) */}
      {polymorphic && methods && methods.length > 0 && (
        <div
          className={cn(
            'mt-2 pl-[36px] pt-[6px] pb-[6px] pr-2',
            'border-t border-dashed border-ia-border-subtle',
            'font-mono text-[9.5px] text-ia-text-tertiary leading-[1.4] tracking-[0.02em]',
          )}
        >
          <span className="text-ia-text-quiet mr-1">handles</span>
          {methods.map((m, i) => (
            <React.Fragment key={m}>
              {i > 0 && <span className="mx-[3px] text-ia-text-quiet">&middot;</span>}
              <span className="text-ia-text-secondary">{m}</span>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Inline-edit children slot */}
      {children && <div className="mt-2.5 flex flex-col gap-1.5">{children}</div>}
    </div>
  ),
)
SystemBlockTile.displayName = 'SystemBlockTile'
